import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames/dedupe";
import { CSSTransition } from "react-transition-group";
import { TSearchClasses, SearchContext } from "../SearchContext";
import SearchResultsPage from "../SearchResults/SearchResultsPage";
import { ISearchResults } from "../SearchResults";
import DropdownList from "../../../Utility/Dropdown/DropdownList";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import SearchInput from "../SearchInput/SearchInput";
import useMakeRequestByURL from "../hooks/useMakeRequestByURL";
import useSetFixed from "../hooks/useSetFixed";
import {
  DropdownAlignment,
  DropdownToggleStrategy,
  DropdownContext,
  IDropdownContext,
} from "../../../Utility";
import { SvgIcon } from "../../../Icons";
import { ExitIcon, SearchIcon } from "../../../../icons";
import DrawerMask from "../../../Utility/DrawerMask/DrawerMask";
import { GRID_BREAKPOINTS } from "../../../Fundamentals";
import {
  useWatchState,
  useDebounce,
  useWindowDimensions,
  useEventListener,
} from "../../../../hooks";
import { getDefaultScrollConfig } from "../../../../helpers";

export interface IHeaderSearchProps {
  /** Is search dropdown fixed */
  nonFixed?: boolean;
  /** Force open / close */
  opened?: boolean;
  /** List of results */
  searchResults?: ISearchResults;
  /** Enable gender-driven search */
  withGender?: boolean;
  /** Show details of result */
  withDetails?: boolean;
  /** Custom classes */
  classes?: TSearchClasses;
  /** Minimal amount of symbols to start search */
  minSearchTermLength?: number;
  /** Called on search */
  onSearch: (searchResult: string | { [key: string]: any }) => void;
  /** Called when search dropdown is opened/closed */
  onToggle?: (newValue: boolean) => void;
  /** Specify do we need to use throttle while making of the request */
  withThrottle?: boolean;
  /** Throttle delay to handle outer onSearch, by default it's 500 */
  delay?: number;
  /** Getting of request URL to which API need to make the request */
  getRequestURL?: (query: string) => string;
  /** execute function when process of the response from the backend */
  onProcessResponse?: (resp: any) => any;
  /** function will be executed when clear current input */
  onClear?: (e?: React.MouseEvent) => void;
  /** heigh of a top menu */
  menuHeight?: number;
  /** id of container main menu */
  menuID?: string;
}

export interface ISearchStateProps {
  onSearch?(searchTerm: string): void;
  onSearchNavigate?: (value: string) => void;
}

export const MIN_SEARCH_TERM_LENGTH = 3;

const MENU_HEIGHT = 52;
const DEBOUNCE_DELAY = 500;
const SEARCH_BOTTOM_OFFSET = 10;
const listenerConfig = getDefaultScrollConfig();

const onPreventDefault = (e: TouchEvent) => e.preventDefault();

const HeaderSearch: FC<IHeaderSearchProps> = ({
  nonFixed,
  opened: parentOpened,
  withGender,
  withDetails,
  searchResults,
  classes,
  minSearchTermLength = MIN_SEARCH_TERM_LENGTH,
  onToggle,
  onSearch,
  withThrottle = false,
  delay = DEBOUNCE_DELAY,
  getRequestURL = () => ``,
  onProcessResponse = (resp) => resp,
  onClear,
  menuHeight = MENU_HEIGHT,
  menuID,
}) => {
  const searchInputRef = useRef<HTMLInputElement>();
  const maskRef = useRef();
  const containerRef = useRef();
  const [toggled, setToggled] = useWatchState({ value: parentOpened });
  const [searchText, setSearchText] = useState<string>(``);
  const [menuOffsetTop, setMenuOffsetTop] = useState(menuHeight);
  const [menuOffsetBottom, setMenuOffsetBottom] = useState<number>(menuHeight);
  const [searchBottomOffset, setSearchOffsetBottom] =
    useState<number>(menuHeight);
  const debouncedSearch = useDebounce(searchText, delay, withThrottle);
  const context = useContext(SearchContext);
  const viewportSizes = useWindowDimensions();
  const isMobileSearch = viewportSizes.viewportWidth < GRID_BREAKPOINTS.lgm;

  // make async requests with debounce
  useMakeRequestByURL({
    onSearch,
    getRequestURL,
    onProcessResponse,
    searchString: debouncedSearch,
    originalSearchText: searchText,
  });

  // make search with fixed position
  const { hasFixedPosition, fixedPositions } = useSetFixed({
    menuHeight: isMobileSearch ? menuOffsetBottom : menuHeight,
    menuOffset: menuOffsetTop,
    isMobile: isMobileSearch,
  });

  const onScroll = useCallback(() => {
    if (isMobileSearch) {
      const elem = globalThis.document.querySelector(`#${menuID}`);

      setMenuOffsetTop(elem?.getBoundingClientRect().top ?? menuHeight);
      setMenuOffsetBottom(elem?.getBoundingClientRect().bottom || menuHeight);
    }
  }, [isMobileSearch, menuID, menuHeight]);

  useEventListener(`scroll`, onScroll, listenerConfig);

  const handleNavigate = useCallback(
    (id: string) => {
      context.handleListItemClick(id);
    },
    [context],
  );

  const handleForceBlur = useCallback(() => {
    searchInputRef.current?.blur();
    context.closeSearch();
    setToggled(false);
  }, [context, setToggled]);

  const handleCloseDropdown = useCallback(() => {
    setToggled(false);
    context.closeSearch();
    onClear?.();
    setSearchText(``);
  }, [context, setToggled, onClear]);

  const handleInputFocus = useCallback(() => {
    context.setActiveSearch();
    if (context.searchTerm.length >= minSearchTermLength) {
      setToggled(true);
    }
  }, [context, minSearchTermLength, setToggled]);

  const handleInputChange = useCallback(
    (value) => {
      context.handleInputChange(value);
      if (context.searchTerm.length >= minSearchTermLength) {
        setToggled(true);
        setSearchText(value);
      }
    },
    [context, minSearchTermLength, setToggled],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      onClear?.(e);
    },
    [onClear],
  );

  const onContainerClick = useCallback(
    (e: React.MouseEvent) => {
      const { target, currentTarget } = e;

      if (target === currentTarget || target === currentTarget.children[0]) {
        // user clicked on padding area in search component
        handleCloseDropdown();
      }
    },
    [handleCloseDropdown],
  );

  const RenderMask = useCallback(
    ({ opened }: IDropdownContext) => (
      <CSSTransition
        in={opened}
        classNames="uds-appear-fade"
        nodeRef={maskRef}
        timeout={300}
        mountOnEnter
        unmountOnExit
      >
        <DrawerMask
          data-testid="NavItemBtn__mask"
          ref={maskRef}
          mods={["header"]}
          style={{ top: `0px` }}
          onClick={handleCloseDropdown}
        />
      </CSSTransition>
    ),
    [handleCloseDropdown],
  );

  useEffect(() => {
    if (!toggled) {
      setToggled(
        context.isActive &&
          context.searchTerm &&
          context.searchTerm.length >= minSearchTermLength,
      );
    }
  }, [
    context.isActive,
    context.searchTerm,
    minSearchTermLength,
    setToggled,
    toggled,
  ]);

  useEffect(() => {
    if (context.isActive) {
      searchInputRef.current?.focus();
    }

    if (context.isActive) {
      globalThis.document.body.style.margin = "0px";
      globalThis.document.body.style.height = "100%";
      globalThis.document.body.style.overflow = "hidden";
    } else {
      globalThis.document.body.style.margin = null;
      globalThis.document.body.style.height = null;
      globalThis.document.body.style.overflow = null;
    }
  }, [context.isActive]);

  useEffect(() => {
    if (context.isActive) {
      setSearchOffsetBottom(
        globalThis.document.querySelector(`#${menuID}`)?.getBoundingClientRect()
          .bottom || menuHeight,
      );
    }
  }, [context.isActive, menuID]);

  useEffect(() => {
    const elem = globalThis.document.querySelector(`#${menuID}`);

    setMenuOffsetTop(elem?.getBoundingClientRect().top ?? menuHeight);
    setMenuOffsetBottom(elem?.getBoundingClientRect().bottom || menuHeight);
  }, [menuID]);

  const handleOpenMobileSearch = useCallback(() => {
    context.setActiveSearch();
  }, [context]);

  useEffect(() => {
    const menu: HTMLDivElement =
      globalThis.document.querySelector(`#${menuID}`) ??
      globalThis.document.createElement(`div`);
    const result: HTMLDivElement =
      containerRef.current ?? globalThis.document.createElement(`div`);

    if (!searchResults?.detailResults?.length) {
      [menu, result].forEach((el) =>
        el.addEventListener("touchmove", onPreventDefault, listenerConfig),
      );
    }
    return () => {
      [menu, result].forEach((el) =>
        el.removeEventListener("touchmove", onPreventDefault),
      );
    };
  }, [menuID, searchResults]);

  return (
    <>
      <div
        className={classNames("icon-holder cursor-pointer header-icon-mobile")}
      >
        <SvgIcon
          icon={SearchIcon}
          classes={{ root: "icon-search" }}
          data-testid="SearchIcon"
          onClick={handleOpenMobileSearch}
        />
      </div>
      <Dropdown
        dropdownButton={
          <DropdownContext.Consumer>
            {({ buttonRef }) => (
              <div
                ref={buttonRef}
                className={classNames("search-header align-items-center", {
                  "d-flex": context.isActive,
                })}
              >
                <div className="search-container flex-grow-1">
                  <SearchInput
                    id="header-search-input"
                    ref={searchInputRef}
                    searchTerm={context.searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onForceBlur={handleForceBlur}
                    onSearchSubmit={handleNavigate}
                    onClear={handleClear}
                  />
                </div>
                <CSSTransition
                  classNames="uds-appear-fade"
                  in={context.isActive}
                  timeout={300}
                  mountOnEnter
                  unmountOnExit
                >
                  <button
                    data-testid="HeaderSearch__exit"
                    className="search-exit position-lgm-absolute end-0 top-50 translate-middle-lgm-y"
                    aria-label="Exit search panel"
                    type="button"
                    onClick={handleCloseDropdown}
                  >
                    <SvgIcon icon={ExitIcon} classes={{ root: "icon-exit" }} />
                  </button>
                </CSSTransition>
              </div>
            )}
          </DropdownContext.Consumer>
        }
        classes={{
          button: "flex-grow-1",
        }}
        opened={toggled}
        toggleStrategy={DropdownToggleStrategy.NONE}
        alignment={DropdownAlignment.STRETCH}
        onChange={onToggle}
      >
        <DropdownList
          animation="uds-dropdown-toggle"
          mask={RenderMask}
          classes={{
            root: classNames(
              classes?.dropdownListRoot,
              "h-100 position-fixed min-h-100",
              {
                "d-none": !context.isActive,
              },
            ),
          }}
          hasFixedDropdown={hasFixedPosition}
          fixedPositions={fixedPositions}
          hasTableDisplay
          onContainerClick={!isMobileSearch ? onContainerClick : undefined}
        >
          {() => (
            <div
              ref={containerRef}
              className={classNames(
                "position-relative",
                classes?.dropdownListContent,
                {
                  "container-fluid flex-grow-1 mb-10": !isMobileSearch,
                  "pt-4": !isMobileSearch && !hasFixedPosition,
                  "pt-1": !isMobileSearch && hasFixedPosition,
                  "h-100 overflow-auto": isMobileSearch,
                },
              )}
              style={
                isMobileSearch
                  ? {
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      paddingBottom: `${menuOffsetBottom}px`,
                    }
                  : null
              }
            >
              <div
                className={classNames({
                  "overflow-auto": !isMobileSearch,
                  "h-100 min-h-100": isMobileSearch,
                })}
                style={
                  !isMobileSearch
                    ? {
                        maxHeight: `calc(100vh - ${
                          searchBottomOffset + SEARCH_BOTTOM_OFFSET
                        }px)`,
                      }
                    : null
                }
              >
                <SearchResultsPage
                  minSearchTermLength={minSearchTermLength}
                  withDetails={withDetails}
                  searchResults={searchResults}
                  isGenderDriven={withGender}
                  nonFixed={nonFixed}
                  closeOnSelection={false}
                  classes={{
                    root: `${isMobileSearch ? "h-100" : ""}`,
                  }}
                />
              </div>
            </div>
          )}
        </DropdownList>
      </Dropdown>
    </>
  );
};

export default HeaderSearch;
